// ============= Imports =============


import { tokenManager } from '@tw/tw-component-library';
import { AuthContextProps } from '../../../contexts/AuthContext';
import { createLogger } from '../../../utils/logger';

// ============= Constants =============
const BASE_URL = import.meta.env.VITE_BASE_URL;
const API_ENDPOINT = '/api/Permissions/me';
const AXIS_SCOPE = 'api://f23d5e95-70a8-4aca-b373-e23d8c9f6454/access_as_user';
const HEADERS = {};

// ============= Logger =============
const logger = createLogger('CurrentUserPermissions');

// ============= Types =============

interface WorkflowType {
  getAccessToken: AuthContextProps['getAccessToken'];
  spoofedUser?: string | null;
}

// ============= Utility Functions =============
/**
 * Constructs filter parameters for API query
 * @param searchValue Search term to filter users
 * @returns Formatted filter string
 */
// const constructFilterParams = (searchValue: string): string => {
//   return [
//     `startswith(givenName,'${searchValue}')`,
//     `startswith(surname,'${searchValue}')`,
//     `startswith(userPrincipalName,'${searchValue}')`,
//   ].join(' or ');
// };

/**
 * Creates headers for API request
 * @param token Authentication token
 * @returns Headers object
 */
const createHeaders = (token: string, spoofedUser: string | null) => ({
  Authorization: `Bearer ${token}`,
  ...HEADERS,
  ...(spoofedUser && {
    'x-Spoof-User': spoofedUser,
  }),
});

// ============= Main Function =============
/**
 * Fetches users from Microsoft API based on search criteria
 * @param getAccessToken Function to acquire access token
 * @param searchValue Search string to filter users
 * @returns Promise with API response containing user data or error
 */
export const currentUserPermissions = async ({
  getAccessToken,
  spoofedUser,
}: WorkflowType) => {
  try {
    logger.debug('Fetching API data');

    // Token acquisition with required scopes

    const token = await getAccessToken([AXIS_SCOPE]);
    if (!token) {
      throw new Error('Failed to acquire API token');
    }
    tokenManager.logTokenInfo(token, 'API');

    // URL construction
    const url = `${BASE_URL}${API_ENDPOINT}`;
    const convertURI = encodeURI(url);
    logger.debug('Making request to API', { url: convertURI });

    // API request
    const response = await fetch(convertURI, {
      method: 'GET',
      headers: createHeaders(token, spoofedUser || null),
    });

    // Response handling
    if (!response.ok) {
      logger.error('HTTP error occurred', { status: response.status });
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    const data = await response.json();
    logger.debug('API data fetched successfully');

    return {
      success: true,
      data,
    };
  } catch (error) {
    logger.error('Error fetching API data', { error });
    return {
      success: false,
      error:
        error instanceof Error ? error.message : 'An unknown error occurred',
    };
  }
};
